import { useMemo } from 'react';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';
import SingleZipPopover from '../Popovers/SingleZipPopover';
import ServiceAreaMarkerContent from './Content/ServiceAreaMarkerContent';
import { usePlacesAutocomplete } from '../../../hooks/usePlacesAutoComplete';
import { useGlobal } from '../../../hooks';
import {
    useServiceAreaHover,
    useServiceAreaHoverMulti,
} from '../../../hooks/useServiceAreaHover';
import { BaseMarker, HoverState } from './BaseMarker';
import { getPageUrl, useAnalyticsClickEvent, useMap } from 'rwl-ui';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';
import MultiZipPopover from '../Popovers/MultiZipPopover';

type ServiceAreaMarkerProps = {
    position: google.maps.LatLngLiteral;
    serviceArea?: NeighborhoodServiceAreaInfo;
    serviceAreas?: NeighborhoodServiceAreaInfo[];
    onHover?: (state: HoverState) => void;
    onClick?: () => void;
    isCluster?: boolean;
    totalJobs?: number;
};

export const ServiceAreaMarker = ({
    position,
    serviceArea,
    serviceAreas,
    onHover,
    onClick,
    isCluster,
    totalJobs,
}: ServiceAreaMarkerProps) => {
    const { config } = useGlobal();
    const { setSelectedPlaceId } = usePlacesAutocomplete();
    const { map } = useMap();
    const sendClickEvent = useAnalyticsClickEvent();

    const placeId =
        serviceArea?.placeInfo?.place_id ??
        serviceAreas?.[0]?.placeInfo?.place_id;

    // For clusters, collect all placeIds so polygon hover/click works for any member
    const allPlaceIds = useMemo(
        () =>
            serviceAreas
                ?.map((a) => a.placeInfo?.place_id)
                .filter((id): id is string => Boolean(id)) ?? [],
        [serviceAreas]
    );

    const isExternallyHoveredSingle = useServiceAreaHover(
        isCluster ? undefined : placeId
    );
    const isExternallyHoveredMulti = useServiceAreaHoverMulti(
        isCluster ? allPlaceIds : []
    );
    const isExternallyHovered = isCluster
        ? isExternallyHoveredMulti
        : isExternallyHoveredSingle;

    if ((serviceArea && !serviceArea.jobCount) || !map) return null;

    return (
        <BaseMarker
            position={position}
            markerId={placeId}
            additionalMarkerIds={isCluster ? allPlaceIds : undefined}
            isExternallyHovered={isExternallyHovered}
            onMarkerClick={() => {
                // Marker click only opens the popover (handled by BaseMarker)
                // Zoom happens when clicking on the popover content
            }}
            onMarkerHover={(state) => {
                onHover?.(state);
            }}
            markerContent={({ isPopupOpen, isHovered }) => (
                <ServiceAreaMarkerContent
                    icon={config?.account.mapSettings.icon}
                    iconTint={config?.account.mapSettings.color}
                    jobCount={serviceArea?.jobCount || totalJobs || 0}
                    isCluster={isCluster}
                    isPopupOpen={isPopupOpen}
                    isHovered={isHovered}
                />
            )}
            popoverContent={
                isCluster && serviceAreas ? (
                    <MultiZipPopover
                        onClick={(area) => {
                            onClick?.();
                            setSelectedPlaceId(area.placeInfo?.place_id);
                            sendClickEvent({
                                url: getPageUrl(),
                                target: ClickTargets.SERVICE_AREA,
                            });
                        }}
                        serviceAreas={serviceAreas}
                    />
                ) : (
                    serviceArea && (
                        <SingleZipPopover
                            serviceArea={serviceArea}
                            onClick={() => {
                                onClick?.();
                                setSelectedPlaceId(
                                    serviceArea.placeInfo?.place_id
                                );
                                sendClickEvent({
                                    url: getPageUrl(),
                                    target: ClickTargets.SERVICE_AREA,
                                });
                            }}
                        />
                    )
                )
            }
            popoverAnchor="bestCenter"
        />
    );
};
